import React from 'react';
import {Avatar, Button, Dropdown, Grid, Layout, Space} from 'antd';
import {
    AppleFilled,
    DownloadOutlined,
    HeartOutlined,
    LoginOutlined,
    LogoutOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    MoonOutlined,
    SettingOutlined,
    SunOutlined,
    UserOutlined,
} from '@ant-design/icons';
import {Link, useNavigate} from 'react-router-dom';
import {useAuth} from '../../contexts/AuthContext';
import {useTheme} from '../ThemeProvider';
import SearchBar from '../Search/SearchBar';
import GooglePlayIcon from '../Icons/GooglePlayIcon';
import {APP_LINKS, USE_USER} from '../../config/features';
import './TopBar.css';

const { Header } = Layout;
const { useBreakpoint } = Grid;

interface TopBarProps {
  collapsed: boolean;
  onToggleSidebar: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ collapsed, onToggleSidebar }) => {
  const { token, logout, user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'preferences',
      icon: <HeartOutlined />,
      label: 'Preferences',
      onClick: () => navigate('/preferences'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  const appMenuItems = [
    {
      key: 'ios',
      icon: <AppleFilled />,
      label: 'App Store',
      onClick: () => window.open(APP_LINKS.ios, '_blank'),
    },
    {
      key: 'android',
      icon: <GooglePlayIcon />,
      label: 'Google Play',
      onClick: () => window.open(APP_LINKS.android, '_blank'),
    },
  ];

  return (
    <Header
      className="topbar"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 998,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        padding: screens.md ? '0 24px' : '0 16px',
        background: isDark ? '#141414' : '#fff',
        borderBottom: isDark ? '1px solid #303030' : '1px solid #f0f0f0',
      }}
    >
      {/* Left: Toggle Button */}
      <Button
        type="text"
        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        onClick={onToggleSidebar}
        style={{
          fontSize: 18,
          width: 48,
          height: 48,
          color: isDark ? '#fff' : '#000',
          flexShrink: 0,
        }}
      />

      {/* Center: Search Bar */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: screens.md ? '0 24px' : '0 8px',
        }}
      >
        <div style={{ width: '100%', maxWidth: 600 }}>
          <SearchBar placeholder="Search news..." size="middle" />
        </div>
      </div>

      {/* Right: Theme Toggle + User/App Links */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        <Space size="middle" align="center">
          <Button
            type="text"
            icon={isDark ? <SunOutlined /> : <MoonOutlined />}
            onClick={toggleTheme}
            style={{
              fontSize: 18,
              width: 40,
              height: 40,
              color: isDark ? '#faad14' : '#8c8c8c',
            }}
          />
          {USE_USER ? (
            token ? (
              <Dropdown
                menu={{ items: userMenuItems }}
                placement="bottomRight"
                arrow
              >
                <Space style={{ cursor: 'pointer' }}>
                  <Avatar
                    icon={<UserOutlined />}
                    style={{
                      backgroundColor: isDark ? '#177ddc' : '#096dd9',
                      color: '#fff',
                    }}
                  />
                  {screens.md && user && (
                    <span style={{ color: isDark ? '#fff' : '#000', whiteSpace: 'nowrap' }}>
                      {user.fullname || user.username}
                    </span>
                  )}
                </Space>
              </Dropdown>
            ) : (
              <Link to="/login">
                <Button
                  type="primary"
                  icon={<LoginOutlined />}
                  style={{
                    background: isDark ? '#177ddc' : '#096dd9',
                    borderColor: isDark ? '#177ddc' : '#096dd9',
                  }}
                >
                  {screens.md && 'Login'}
                </Button>
              </Link>
            )
          ) : (
            <Dropdown
              menu={{ items: appMenuItems }}
              placement="bottomRight"
              arrow
            >
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                style={{
                  background: isDark ? '#177ddc' : '#096dd9',
                  borderColor: isDark ? '#177ddc' : '#096dd9',
                }}
              >
                {screens.md && 'Get the App'}
              </Button>
            </Dropdown>
          )}
        </Space>
      </div>
    </Header>
  );
};

export default TopBar;
